import logger from '../utils/logger';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check } from 'lucide-react';
import { themes } from '../data/themes';
import { themeService } from '../services/themeService';
import MenuPreview from './MenuPreview';
import { useShop } from '../context/ShopContext';
import toast from 'react-hot-toast';
import api from '../config/api';

const MenuCustomization = ({ onClose }) => {
  const { t } = useTranslation();
  const [selectedTheme, setSelectedTheme] = useState(themeService.getCurrentTheme());
  const { shop } = useShop();

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme);
  };

  const handleApply = async () => {
    try {
      themeService.applyTheme(selectedTheme.id);
      
      const newCustomization = {
        themeId: selectedTheme.id,
        colors: {},
        typography: {},
        layout: {},
        ...selectedTheme.layout
      };
      
      themeService.saveCustomization(newCustomization);
      // Persist to backend so the theme applies to all visitors
      try {
        await api.put('/shop', {
          menuThemeId: selectedTheme.id,
          menuCustomization: JSON.stringify(newCustomization)
        });
      } catch (persistErr) {
  logger.warn('Failed to persist theme to backend (owner only change will still apply locally):', persistErr);
      }
      
      toast.success(t('dashboard.themeAppliedSuccessfully'));
      onClose();
    } catch (error) {
  logger.error('Error applying theme:', error);
      toast.error(t('dashboard.failedToApplyTheme'));
    }
  };

  const groupedThemes = themes.reduce((groups, theme) => {
    const category = theme.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(theme);
    return groups;
  }, {});

  const sortedGroups = Object.keys(groupedThemes).sort((a, b) => {
    if (a === 'Default') return -1;
    if (b === 'Default') return 1;
    return a.localeCompare(b);
  }).reduce((obj, key) => {
    obj[key] = groupedThemes[key];
    return obj;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-b from-[#1f2937] to-[#111827] rounded-2xl shadow-2xl border border-gray-700/50 w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
          <div>
            <h2 className="text-2xl font-bold text-white">{t('dashboard.menuThemes')}</h2>
            <p className="text-gray-300 mt-1">{t('dashboard.chooseThemeDesign')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700/50 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-300 hover:text-white" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-1/2 p-6 overflow-y-auto border-r border-gray-700/50">
            <div className="space-y-8">
              {Object.entries(sortedGroups).map(([category, categoryThemes]) => (
                <div key={category}>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    {category}
                    {category === 'Default' && (
                      <span className="text-xs bg-[#a15507] text-white px-2 py-1 rounded-full font-medium">
                        {t('dashboard.recommended')}
                      </span>
                    )}
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {categoryThemes.map((theme) => (
                      <div
                        key={theme.id}
                        onClick={() => handleThemeSelect(theme)}
                        className={`relative cursor-pointer rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                          selectedTheme.id === theme.id
                            ? 'border-[#a15507] shadow-lg shadow-[#a15507]/30 ring-2 ring-[#a15507]/50'
                            : 'border-gray-600 hover:border-gray-500 bg-[#374151]/20 hover:bg-[#374151]/40'
                        }`}
                      >
                        <div 
                          className="p-6 rounded-xl"
                          style={{ 
                            background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
                          }}
                        >
                          <div 
                            className="bg-white/90 backdrop-blur-sm rounded-lg p-4 mb-4"
                            style={{ 
                              fontFamily: theme.typography.fontFamily,
                            }}
                          >
                            <h4 
                              className="font-bold mb-2"
                              style={{ 
                                color: theme.colors.text,
                                fontSize: `${theme.typography.headingSize - 8}px`
                              }}
                            >
                              {t('dashboard.sampleCoffee')}
                            </h4>
                            <p 
                              className="text-sm mb-2"
                              style={{ color: theme.colors.textSecondary }}
                            >
                              {theme.layout.showDescriptions ? t('dashboard.richEspressoBlend') : ''}
                            </p>
                            <div className="flex justify-between items-center">
                              <span 
                                className="font-bold"
                                style={{ color: theme.colors.accent }}
                              >
                                $4.50
                              </span>
                              <div 
                                className="px-3 py-1 rounded text-xs font-medium"
                                style={{ 
                                  backgroundColor: theme.colors.primary,
                                  color: theme.colors.surface
                                }}
                              >
                                {t('menu.add')}
                              </div>
                            </div>
                          </div>

                          <div className="text-white">
                            <h3 className="text-xl font-bold mb-2">{theme.name}</h3>
                            <p className="text-sm opacity-90 mb-3">
                              {theme.description}
                            </p>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="opacity-80">{t('dashboard.layout')}:</span>
                                <br />
                                <span className="font-medium">
                                  {theme.layout.layoutStyle === 'featured' ? t('dashboard.featuredHero') : 
                                   theme.layout.layoutStyle === 'sheet' ? t('dashboard.menuSheet') :
                                   theme.layout.productCardStyle === 'vertical' ? t('dashboard.gridCards') : t('dashboard.listView')}
                                </span>
                              </div>
                              <div>
                                <span className="opacity-80">{t('dashboard.font')}:</span>
                                <br />
                                <span className="font-medium">
                                  {theme.typography.fontFamily.split(',')[0]}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {selectedTheme.id === theme.id && (
                          <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-full p-2">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="w-1/2 p-6 flex flex-col">
            <h3 className="text-lg font-semibold text-white mb-4">{t('dashboard.livePreview')}</h3>
            <div className="flex-1 border border-gray-600 rounded-lg overflow-hidden min-h-0 bg-black/20">
              <MenuPreview 
                currentTheme={selectedTheme} 
                customization={selectedTheme.layout}
                shopName={shop?.name || t('dashboard.yourCoffeeShop')}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-700/50 bg-[#1f2937]/80 backdrop-blur-sm">
          <div className="text-sm text-gray-300">
            {t('dashboard.selectedTheme')}: <span className="font-medium text-white">{selectedTheme.name}</span>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-300 bg-[#374151] border border-gray-600 rounded-lg hover:bg-[#4B5563] hover:text-white transition-all duration-200 font-medium"
            >
              {t('dashboard.cancelButton')}
            </button>
            <button
              onClick={handleApply}
              className="px-6 py-2 bg-[#a15507] text-white rounded-lg hover:bg-[#924e06] transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t('dashboard.applyThemeButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuCustomization;